import { useId, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@components/ui/Button';
import { Input } from '@components/ui/Input';
import { AuthLayout } from './AuthLayout';
import { useSignIn, useSignInWithGoogle } from './hooks/useAuth';
import { completeMcpHandoff, stashMcpParamsIfPresent } from './mcp-handoff';

const schema = z.object({
  email: z.string().min(1, 'E-posta zorunlu').email('Geçerli bir e-posta gir'),
  sifre: z.string().min(1, 'Şifre zorunlu'),
});
type FormData = z.infer<typeof schema>;

export function LoginPage() {
  const { t } = useTranslation('auth');
  const nav = useNavigate();
  const signIn = useSignIn();
  const googleSignIn = useSignInWithGoogle();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const emailId = useId();
  const sifreId = useId();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = handleSubmit(async (data) => {
    setSubmitError(null);
    try {
      await signIn.mutateAsync({ email: data.email, sifre: data.sifre });
      // MCP "Zoprey'e bağlan" akışı aktifse handoff'u tamamla; değilse dashboard.
      if (await completeMcpHandoff()) return;
      nav('/dashboard');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Giriş başarısız';
      setSubmitError(
        msg.toLowerCase().includes('invalid')
          ? t('login.hata_credentials')
          : msg,
      );
    }
  });

  return (
    <AuthLayout title={t('login.baslik')} subtitle={t('login.alt_baslik')}>
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor={emailId} className="block text-sm font-medium mb-1.5">
            {t('login.email')}
          </label>
          <Input
            id={emailId}
            type="email"
            autoComplete="email"
            placeholder="ornek@firmaniz.com"
            {...register('email')}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-danger">{errors.email.message}</p>
          )}
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor={sifreId} className="block text-sm font-medium">
              {t('login.sifre')}
            </label>
            <Link
              to="/auth/forgot-password"
              className="text-xs text-primary hover:underline"
            >
              {t('login.sifremi_unuttum')}
            </Link>
          </div>
          <Input
            id={sifreId}
            type="password"
            autoComplete="current-password"
            {...register('sifre')}
            aria-invalid={!!errors.sifre}
          />
          {errors.sifre && (
            <p className="mt-1 text-xs text-danger">{errors.sifre.message}</p>
          )}
        </div>

        {submitError && (
          <div className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger">
            {submitError}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isSubmitting}>
          {isSubmitting ? t('login.giriliyor') : t('login.giris_yap')}
        </Button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">
              {t('login.veya')}
            </span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => {
            // Google redirect'i öncesi MCP parametrelerini sakla (callback'te tamamlanır).
            stashMcpParamsIfPresent();
            googleSignIn.mutate();
          }}
          disabled={googleSignIn.isPending}
        >
          {/* Google ikonu inline svg */}
          <svg className="h-4 w-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#EA4335" d="M5.27 9.76A7.05 7.05 0 0 1 12 5c1.88 0 3.55.65 4.85 1.74L20.06 3.5A11.86 11.86 0 0 0 12 .5a11.96 11.96 0 0 0-10.7 6.6l4 3.1z"/>
            <path fill="#34A853" d="M16.05 18.31c-1.1.74-2.5 1.18-4.05 1.18a7.06 7.06 0 0 1-6.72-4.78l-4.1 3.16A12.13 12.13 0 0 0 12 23.5c2.96 0 5.6-1 7.45-2.68l-3.4-2.5z"/>
            <path fill="#4A90E2" d="M19.45 20.82c1.92-1.77 3.05-4.36 3.05-7.43 0-.68-.07-1.34-.18-1.97H12v3.94h5.83a4.94 4.94 0 0 1-2.18 3.26l3.4 2.2z"/>
            <path fill="#FBBC05" d="M5.28 14.7a7.13 7.13 0 0 1 0-5.4L1.18 6.14a12.04 12.04 0 0 0 0 11.72l4.1-3.16z"/>
          </svg>
          {t('login.google_ile_giris')}
        </Button>

        <p className="text-center text-sm text-muted-foreground mt-6">
          {t('login.hesap_yok')}{' '}
          <Link to="/auth/register" className="text-primary hover:underline">
            {t('login.kayit_ol')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}
